import React from 'react'
import type { ComponentProps } from 'react'
import dynamic from 'next/dynamic'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import type { ContactForm as EnterpriseContactForm } from '@mc/enterprise-contact-form'
import { useTranslation } from '@mc/i18n'
import { EnterpriseGrowthEvents, EventTypes, PageSource } from '@mc/track-event'
import Image from 'components/LegacyImage'
import { useSprig } from 'libs/sprig'
import { AboutCards } from './components/AboutCards'
import { EmailCTA } from './components/EmailCTA'
import { ExploreCards } from './components/ExploreCard'
import Faq from './components/Faq'
import { Head } from './components/Head'
import { SectionHeading } from './components/SectionHeading'
import { SelfServe } from './components/SelfServe'
import { trackPurchaseNowClick } from './components/SelfServe/utils/events'
import { StatisticsCards } from './components/StatisticsCard'
import { VideoContainer } from './components/VideoContainer'
import CapitalOne from './components/assets/capital_one.svg'
import Deloitte from './components/assets/deloitte.svg'
import Microsoft from './components/assets/microsoft.svg'
import PayPal from './components/assets/paypal.svg'
import Square from './components/assets/square.svg'
import { trackEnterpriseContactUsClicked } from './components/events'
import styles from './index.module.scss'
import { getPageLoadIntegrations } from './integrations'

const ContactForm = dynamic<ComponentProps<typeof EnterpriseContactForm>>(
  import(
    /* webpackChunkName: 'enterprise-landing-contact-form' */
    '@mc/enterprise-contact-form'
  ).then((pkg) => pkg.ContactForm),
  {
    ssr: false,
  },
)

const client = new QueryClient()

export const getServerSideProps = () => ({ props: {} })

const ForBusiness = () => {
  const { t } = useTranslation('@mc/next')

  useSprig()

  return (
    <>
      <Head />
      <QueryClientProvider client={client}>
        <div className='d-flex flex-column container mc-mt-12'>
          <Container className='mc-gap-9'>
            <Image
              src='https://static.masterclass.com/for-business-hero.png'
              width={1768}
              height={272}
              alt=''
            />
            <h1 className='mc-font-headline1 mc-text--center mc-mt-10 col-md-10'>
              {t('forBusiness.heading')}
            </h1>
            <h2 className='mc-text-large mc-text--center col-md-8'>
              {t('forBusiness.subheading')}
            </h2>
            <EmailCTA />
          </Container>
          <VideoContainer
            className='mc-my-12'
            mediaUuid='38c58716-6dc0-4d36-a39e-1f646d3df1dd'
          />
          <div className='mc-my-12'>
            <div className={'d-flex flex-column mc-m-auto col-md-10 mc-gap-6'}>
              <SectionHeading label={t('forBusiness.aboutSection.title')}>
                {t('forBusiness.aboutSection.subtitle')}
              </SectionHeading>
            </div>
            <Container className='mc-mt-8'>
              <AboutCards />
            </Container>
          </div>
          <div className='mc-my-12'>
            <SectionHeading label={t('forBusiness.statsSection.title')}>
              {t('forBusiness.statsSection.subtitle')}
            </SectionHeading>
            <Container
              className={`uncontainer mc-mt-10 mc-p-6 ${styles.jockoCoverImage}`}
            >
              <div>
                <StatisticsCards />
                <div
                  className='d-flex flex-column mc-text--center'
                  style={{ marginTop: '330px' }}
                >
                  <div className='mc-font-headline2 mc-text--bold mc-mb-6'>
                    {t('forBusiness.trusted')}
                  </div>
                  <div className='d-flex flex-wrap justify-content-center align-items-center mc-my-8 mc-text-color--medium mc-gap-5 mc-column-gap-10'>
                    <PayPal />
                    <Microsoft />
                    <CapitalOne />
                    <Square />
                    <Deloitte />
                  </div>
                </div>
              </div>
            </Container>
          </div>
          <div className='mc-my-12'>
            <SectionHeading
              className='mc-mb-10'
              label={t('forBusiness.exploreCardsSection.title')}
            >
              {t('forBusiness.exploreCardsSection.subtitle')}
            </SectionHeading>
            <div className='d-flex flex-column flex-md-row mc-gap-4'>
              <ExploreCards />
            </div>
          </div>
          <div className='mc-my-12'>
            <Container>
              <SectionHeading
                label={t('forBusiness.successStorySection.title')}
              >
                {t('forBusiness.successStorySection.subtitle')}
              </SectionHeading>
            </Container>
            <VideoContainer
              className='mc-my-9'
              mediaUuid='37679e1c-be7a-4a64-86d3-d10e961a8f3f'
            />
            <Container>
              <div className='mc-text--center mc-text--bold mc-text-large mc-text-color--medium mc-px-10'>
                {t('forBusiness.successStorySection.quote')}
              </div>
            </Container>
          </div>
          <div className='mc-my-12'>
            <Container>
              <div
                id='contact-form'
                className='d-flex flex-column justify-content-center mc-bg-container-dark mc-p-9 mc-corners--lg mc-w-100 mc-mt-10'
              >
                <SectionHeading
                  className='mc-mb-10'
                  label={t('forBusiness.connectWithUs')}
                >
                  {t('forBusiness.bringOut')}
                </SectionHeading>
                <ContactForm
                  envName={process.env.NODE_ENV}
                  onClickSubmit={() =>
                    trackEnterpriseContactUsClicked({
                      pageSource: PageSource.MAW_HOME,
                    })
                  }
                />
              </div>
            </Container>
          </div>
          <div className='mc-my-9'>
            <SelfServe
              onCTAClicked={() =>
                trackPurchaseNowClick({
                  pageSource: PageSource.MAW_HOME,
                })
              }
            />
          </div>
          <Faq
            brandedTitle={false}
            trackProps={{
              eventType: EnterpriseGrowthEvents.EVENTS.MAW_HOME_PAGE_CLICK,
              pageSource: EventTypes.FOR_BUSINESSES,
            }}
          />
        </div>
      </QueryClientProvider>
    </>
  )
}

const Container = ({
  children,
  style,
  className,
}: {
  children: React.ReactNode
  style?: React.CSSProperties
  className?: string
}) => (
  <div
    className={`d-flex flex-wrap justify-content-center ${className ?? ''}`}
    style={style}
  >
    {children}
  </div>
)

ForBusiness.pageSettings = {
  title: 'MasterClass at Work',
  rumLabel: 'MAW Landing',
  analyticsLabel: EventTypes.ENTERPRISE_LANDING_PAGE,
  pageLoadIntegrations: getPageLoadIntegrations(true),
}

export default ForBusiness
